import { Checkbox } from "@shared/ui/checkbox";
import { CreditCard, HelpCircle } from "lucide-react";
import React from "react";

interface ApplyCreditBalanceProps {
  availableCredit: number;
  isApplied: boolean;
  onToggle: (checked: boolean) => void;
  currency: string;
  billAmount: number;
  creditApplied: number;
  remainingCredit: number;
  totalPayable: number;
}

export const ApplyCreditBalance: React.FC<ApplyCreditBalanceProps> = ({
  availableCredit,
  isApplied,
  onToggle,
  currency,
  billAmount,
  creditApplied,
  remainingCredit,
  totalPayable,
}) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded bg-[#eff5ff] flex items-center justify-center">
          <CreditCard className="w-4 h-4 text-blue-500" />
        </div>
        <h3 className="text-[16px] font-bold text-gray-900 flex items-center gap-1.5">
          Apply Credit Balance
          <HelpCircle className="w-3.5 h-3.5 text-gray-400" />
        </h3>
      </div>

      {!isApplied ? (
        // Collapsed view - just shows available credit
        <div className="flex items-center justify-between p-4 bg-[#eff5ff] border border-blue-200/60 rounded-lg">
          <div>
            <p className="text-[11px] text-gray-500 mb-0.5 font-medium">
              Available Credit Balance
            </p>
            <p className="text-[16px] font-bold text-blue-600">
              {currency} {availableCredit?.toFixed(2) ?? "0.00"}
            </p>
          </div>

          <label className="flex items-center gap-2 cursor-pointer bg-white border border-gray-200 rounded-md px-3 py-1.5 shadow-sm hover:bg-gray-50 transition-colors">
            <Checkbox
              checked={isApplied}
              onCheckedChange={onToggle}
              className="w-4 h-4 border-gray-300"
            />
            <span className="text-[12px] font-medium text-gray-700">
              Apply to Payment
            </span>
          </label>
        </div>
      ) : (
        // Expanded view - shows breakdown
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-[#eff5ff] rounded-lg border border-blue-200/60">
            <div>
              <p className="text-[11px] text-gray-500 mb-0.5 font-medium">
                Available Credit Balance
              </p>
              <p className="text-[16px] font-bold text-blue-600">
                {currency} {availableCredit?.toFixed(2) ?? "0.00"}
              </p>
            </div>

            <label className="flex items-center gap-2 cursor-pointer bg-white border border-gray-200 rounded-md px-3 py-1.5 shadow-sm hover:bg-gray-50 transition-colors">
              <Checkbox
                checked={isApplied}
                onCheckedChange={onToggle}
                className="w-4 h-4 border-gray-300"
              />
              <span className="text-[12px] font-medium text-gray-700">
                Apply to Payment
              </span>
            </label>
          </div>

          {/* Breakdown */}
          <div className="space-y-2 px-4">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Bill Amount</span>
              <span className="font-medium text-gray-900">
                {currency} {billAmount}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Credit Applied</span>
              <span className="font-medium text-green-600">
                - {currency} {creditApplied}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Remaining Credit</span>
              <span className="font-medium text-gray-900">
                {currency} {remainingCredit}
              </span>
            </div>
            <div className="flex justify-between text-base font-semibold pt-2 border-t border-gray-200">
              <span className="text-gray-900">Total Payable Amount</span>
              <span className="text-blue-600">
                {currency} {totalPayable}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
